import {
	applyHeadlineToUnitPrice,
	formatCurrency,
} from '@supply-mover-2-0/core';
import type {
	ItemPromoHeadline,
	PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { ImagePlaceholder } from '../ImagePlaceholder';
import { ResilientImage } from '../ResilientImage';
import { handleCardLinkClick } from './cardLinkClick';

/**
 * The storefront's item card: image, discount pill, title, manufacturer and
 * promo-aware price. Shared so the storefront and the CRM's page builder
 * render the same card (PD-16588).
 *
 * Everything that only exists inside the storefront app is passed in rather
 * than imported: the add-to-cart control arrives as `actions`, and whether
 * this visitor may see prices arrives as `showPrice` (the storefront reads it
 * from its display settings; the builder canvas shows prices).
 *
 * `showImage` (PD-17144, default true) drops the picture box for brands that
 * hide item images. The discount pill and the actions then move into the card
 * body, so a sale and the cart control still show.
 *
 * `href` (PD-17174) is the product page's address. With it, the picture and
 * the title are real links, so search engines can follow them and shoppers
 * can open the product in a new tab; a plain click still goes through
 * `onNavigate`. The whole card stays clickable either way. Without it (the
 * page builder canvas) the card renders exactly as before.
 */
export function StorefrontItemCard({
	item,
	href,
	onNavigate,
	onLikelyStaleUrl,
	actions,
	showPrice,
	showImage = true,
}: {
	item: PortalStorefrontItemCardDto;
	href?: string;
	onNavigate?: () => void;
	onLikelyStaleUrl?: () => void;
	/** Floating control over the image's bottom-right corner (the cart). */
	actions?: ReactNode;
	showPrice: boolean;
	/** False removes the picture and its box (PD-17144). Default true. */
	showImage?: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const showBadge =
		item.promoHeadline != null && item.promoHeadline.displayBadgeInLists;
	const image = (
		<ResilientImage
			src={item.primaryImageUrl}
			alt={item.description}
			className="w-full h-full object-contain"
			loading="lazy"
			onLikelyStaleUrl={onLikelyStaleUrl}
			fallback={<ImagePlaceholder />}
		/>
	);
	return (
		<div
			// With real links inside, the title link is the keyboard path, so the
			// card itself is only a larger click target (no second tab stop).
			role={href == null ? 'button' : undefined}
			tabIndex={href == null ? 0 : undefined}
			onClick={onNavigate}
			onKeyDown={
				href == null
					? (e) => {
							if (onNavigate != null && (e.key === 'Enter' || e.key === ' ')) {
								e.preventDefault();
								onNavigate();
							}
						}
					: undefined
			}
			className="w-full text-left bg-brand-surface border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
		>
			{showImage && (
				<div className="relative aspect-square bg-brand-input flex items-center justify-center">
					{href != null ? (
						<a
							href={href}
							onClick={(event) => {
								handleCardLinkClick(event, onNavigate);
							}}
							// The title link below is the one in the tab order.
							tabIndex={-1}
							className="block w-full h-full"
						>
							{image}
						</a>
					) : (
						image
					)}
					{showBadge && item.promoHeadline != null && (
						<span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[10px] sm:text-xs font-bold shadow-sm bg-warning-badge text-warning-tint-fg border border-warning-tint">
							{t(item.promoHeadline.badgeText)}
						</span>
					)}
					{actions != null && (
						<div className="absolute bottom-1 right-1 z-10">{actions}</div>
					)}
				</div>
			)}
			<div className="p-2 space-y-1">
				{/* No picture to sit on: the discount pill leads the body. */}
				{!showImage && showBadge && item.promoHeadline != null && (
					<div>
						<span className="inline-flex px-1.5 py-0.5 rounded-md text-[10px] sm:text-xs font-bold bg-warning-badge text-warning-tint-fg border border-warning-tint">
							{t(item.promoHeadline.badgeText)}
						</span>
					</div>
				)}
				<p className="text-xs font-medium text-default line-clamp-2 min-h-[2.25rem]">
					{href != null ? (
						<a
							href={href}
							onClick={(event) => {
								handleCardLinkClick(event, onNavigate);
							}}
							className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
						>
							{t(item.description)}
						</a>
					) : (
						t(item.description)
					)}
				</p>
				{item.manufacturer != null && (
					<p className="text-[11px] text-brand-muted truncate">
						{t(item.manufacturer)}
					</p>
				)}
				{showPrice && (
					<StorefrontItemPrice
						price={item.price}
						contractPrice={item.contractPrice}
						promoHeadline={item.promoHeadline}
					/>
				)}
				{/* No picture corner to float over: the actions get their own
				    right-aligned row at the foot of the card. */}
				{!showImage && actions != null && (
					<div className="flex justify-end pt-1">{actions}</div>
				)}
			</div>
		</div>
	);
}

/**
 * Promo-aware price block for the storefront item card. Renders nothing when
 * the price is unknown so the price slot collapses cleanly. Callers decide
 * whether this visitor may see prices at all.
 */
export function StorefrontItemPrice({
	price,
	contractPrice,
	promoHeadline,
}: {
	price: string | null;
	contractPrice: string | null;
	promoHeadline: ItemPromoHeadline | null;
}): JSX.Element {
	const { t } = useTranslation();
	const listNum = price != null ? parseFloat(price) : NaN;
	const contractNum = contractPrice != null ? parseFloat(contractPrice) : NaN;
	// The contract price wins over the list price, as it always has here.
	const basePrice = Number.isFinite(contractNum)
		? contractNum
		: Number.isFinite(listNum)
			? listNum
			: NaN;
	if (!Number.isFinite(basePrice)) {
		return <></>;
	}
	const promoFinal =
		promoHeadline != null
			? applyHeadlineToUnitPrice(basePrice, promoHeadline)
			: null;
	const promoActive = promoFinal != null && promoFinal < basePrice;

	if (promoActive) {
		return (
			<div className="flex items-center gap-1 flex-wrap">
				<span className="inline-flex items-baseline px-1.5 py-0.5 rounded-md text-default text-xs font-bold bg-warning-badge text-warning-tint-fg border border-warning-tint">
					{t(formatCurrency(promoFinal))}
				</span>
				<span className="text-[10px] text-faint line-through">
					{t(formatCurrency(basePrice))}
				</span>
			</div>
		);
	}

	return (
		<p className="text-sm font-semibold text-default">
			{t(formatCurrency(basePrice))}
		</p>
	);
}
